import { useEffect, useState, type ReactNode } from 'react'
import { Button, Input, Typography } from 'antd'
import {
  health,
  getStoredPreviewAccessKey,
  setStoredPreviewAccessKey,
  verifyPreviewAccessKey,
  PREVIEW_ACCESS_REQUIRED_EVENT,
} from '../api/client'

/**
 * VistoeraTASK-00107 P0-B — Mobile Capture QR pairing must never depend on
 * this gate. Root cause of "scan QR → phone page shows no change": the
 * phone opens `app-preview.vistoera.com/mobile-capture/pair/:token` in a
 * *brand-new* browser context (Safari via the Camera app / a fresh tab)
 * that has never unlocked this generic Preview password on that device —
 * so this gate rendered its own "该预览环境需要访问口令" prompt instead of
 * `MobileCapturePairPage` ever mounting, for every single scan, looking
 * identical each time (hence "no change"). Mobile Capture already has its
 * own independent, stronger, purpose-built auth (short-lived one-time
 * pairing token → scoped `MobileAuthSession`, see
 * `mobile_capture_service.pair_with_token` / P33/P36/P37) — layering this
 * *shared-secret* preview password on top of a scan-to-pair flow that must
 * work for an end user who was never given that secret defeats the whole
 * feature. Exempt the entire `/mobile-capture` subtree unconditionally.
 */
function isMobileCaptureExemptPath(pathname: string): boolean {
  return pathname === '/mobile-capture' || pathname.startsWith('/mobile-capture/')
}

/**
 * Online Preview v0.1 — Basic Preview Access Protection (spec §19).
 *
 * A no-op wrapper (renders children immediately) whenever the backend
 * reports `preview_access_enabled: false` (the default for local dev and
 * any deployment that never set PREVIEW_ACCESS_PASSWORD). Only shows a
 * password prompt when the backend has actually turned the gate on —
 * this component never invents its own notion of "protected".
 */
export default function PreviewAccessGate({ children }: { children: ReactNode }) {
  const exemptPath =
    typeof window !== 'undefined' && isMobileCaptureExemptPath(window.location.pathname)
  const [state, setState] = useState<'checking' | 'open' | 'locked'>(exemptPath ? 'open' : 'checking')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (exemptPath) return
    let cancelled = false

    async function check() {
      try {
        const res = await health()
        if (cancelled) return
        if (!res.data?.preview_access_enabled) {
          setState('open')
          return
        }
        const stored = getStoredPreviewAccessKey()
        if (stored) {
          try {
            await verifyPreviewAccessKey(stored)
            if (!cancelled) setState('open')
            return
          } catch {
            setStoredPreviewAccessKey(null)
          }
        }
        if (!cancelled) setState('locked')
      } catch {
        // /api/health itself unreachable — don't get stuck on a gate that
        // can never resolve; let the app mount and surface the real
        // connectivity error through its normal error handling instead.
        if (!cancelled) setState('open')
      }
    }

    check()
    const onRequired = () => setState('locked')
    window.addEventListener(PREVIEW_ACCESS_REQUIRED_EVENT, onRequired)
    return () => {
      cancelled = true
      window.removeEventListener(PREVIEW_ACCESS_REQUIRED_EVENT, onRequired)
    }
  }, [])

  async function handleSubmit() {
    if (!password || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await verifyPreviewAccessKey(password)
      setStoredPreviewAccessKey(password)
      setState('open')
    } catch {
      setError('访问口令不正确，请重试。')
    } finally {
      setSubmitting(false)
    }
  }

  if (state === 'checking') return null
  if (state === 'open') return <>{children}</>

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f2f5',
      }}
    >
      <div
        style={{
          width: 360,
          padding: 32,
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
        }}
      >
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          Vistoera Online Preview
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          该预览环境需要访问口令
        </Typography.Paragraph>
        <Input.Password
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={handleSubmit}
          placeholder="访问口令"
          status={error ? 'error' : undefined}
        />
        {error && (
          <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>
            {error}
          </Typography.Text>
        )}
        <Button type="primary" block style={{ marginTop: 16 }} loading={submitting} onClick={handleSubmit}>
          进入
        </Button>
      </div>
    </div>
  )
}
